/* eslint-disable @typescript-eslint/no-explicit-any */

import storageMiddleware from '@/utils/storageMiddleware'
import { trackEvent } from '@mercadoni/sinister/dist/cjs/monitors/services'
import {
  daysToSeconds,
  device,
} from '@mercadoni/sinister/dist/cjs/shared/utils'
import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'
import { usePathname } from 'next/navigation'
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import clientIsEnabled from './clientIsEnabled'
const DISPLAYED_APP_DOWNLOAD = 'displayedAppDownload'
const DownloadTheAppContext = createContext<DownloadTheAppContextValue | null>(
  null
)

interface DownloadTheAppProviderProps extends PropsWithChildren<any> {
  clientConfig: ClientModel
}
interface DownloadTheAppContextValue {
  isEnabledToDownload: boolean
  enableDownload: () => void
  openDownload: () => void
  cancelDownload: () => void
  storeData: any
}

export const DownloadTheAppProvider: FC<DownloadTheAppProviderProps> = ({
  clientConfig,
  ...props
}) => {
  const { app } = clientConfig
  const [isEnabledToDownload, setIsEnabledToDownload] = useState(false)
  const [deviceInfo, setDeviceInfo] = useState<ReturnType<typeof device>>({})
  const pathname = usePathname()
  const db = useMemo(() => storageMiddleware('local'), [])
  useEffect(() => {
    const info = device()
    setDeviceInfo(info)
  }, [])
  const storeData = useMemo(() => {
    if (
      !clientIsEnabled({
        clientConfiguration: app,
        device: deviceInfo,
      })
    ) {
      return {}
    }

    return {
      deviceOS: deviceInfo?.data?.OS,
      data: deviceInfo?.isAndroid() ? app?.android : app?.ios,
    }
  }, [app, deviceInfo])

  const downloadDisplayed = useCallback(
    () =>
      db.setItem(
        DISPLAYED_APP_DOWNLOAD,
        'true',
        daysToSeconds(app?.timeToReSuggestDownload || 1)
      ),
    [app?.timeToReSuggestDownload, db]
  )

  const openDownload = useCallback(async () => {
    downloadDisplayed()
    trackEvent({
      path: pathname,
      eventName: 'go_to_app',
    })
  }, [downloadDisplayed, pathname])

  const cancelDownload = useCallback(() => {
    downloadDisplayed()
    trackEvent({
      path: pathname,
      eventName: 'continue_in_browser',
    })
  }, [downloadDisplayed, pathname])

  const enableDownload = useCallback(() => {
    setIsEnabledToDownload(
      Object.keys(storeData).length > 0 &&
        db.getItem(DISPLAYED_APP_DOWNLOAD) !== 'true'
    )
  }, [storeData, db])

  const isCSR = typeof window !== 'undefined'
  const accessPoint = isCSR
    ? (window as Window & { accessPoint?: string })?.accessPoint
    : undefined

  const value = useMemo(
    () => ({
      isEnabledToDownload: isEnabledToDownload && accessPoint === 'WEB',
      enableDownload,
      openDownload,
      cancelDownload,
      storeData,
    }),
    [
      isEnabledToDownload,
      accessPoint,
      enableDownload,
      openDownload,
      cancelDownload,
      storeData,
    ]
  )
  return <DownloadTheAppContext.Provider value={value} {...props} />
}

export const useDownloadTheAppContext = () => {
  const context = React.useContext(DownloadTheAppContext)
  if (!context) {
    throw new Error('useDownloadTheAppContext: No context')
  }
  return context
}
